import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Tag, Descriptions, Alert, Progress, Grid, Button, Spin, Modal, message } from 'antd';
import {
  GlobalOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  SettingOutlined,
  LineChartOutlined,
  ClearOutlined
} from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import cdnService from '../../services/cdnService';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import { formatOrigins } from '../../../shared/utils/format';

const { useBreakpoint } = Grid;

export default function Overview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { domainData, subscriptionInfo } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [chartLoading, setChartLoading] = useState(false);
  const [trafficData, setTrafficData] = useState(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // 加载最近7天的流量数据
  useEffect(() => {
    if (domainData?.domain) {
      loadTrafficData();
    }
  }, [domainData]);

  // 初始化/更新图表
  useEffect(() => {
    if (chartRef.current && (trafficData?.timeline || trafficData?.trend)) {
      initChart();
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [trafficData, isMobile]);

  // 窗口resize时更新图表
  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadTrafficData = async () => {
    setChartLoading(true);
    try {
      const startTime = dayjs().subtract(6, 'day').startOf('day').unix(); // Unix 秒:服务端按东八区解析字符串,其他时区会偏移
      const endTime = dayjs().unix();
      const res = await apiClient.get(`/api/user/cdn/domains/${domainData.domain}/traffic`, {
        params: { startTime, endTime }
      });
      if (res.data.success) {
        setTrafficData(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load traffic:', error);
    } finally {
      setChartLoading(false);
    }
  };

  const initChart = () => {
    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    chartInstance.current = echarts.init(chartRef.current);

    const trend = trafficData.timeline || trafficData.trend || [];
    const dates = trend.map(item => {
      const t = item.time_bucket || item.date || item.time;
      if (!t) return t;
      // OpenObserve time_bucket 是 UTC，转换为本地时间显示
      const utcStr = typeof t === 'string' && !t.endsWith('Z') && !t.includes('+') ? t + 'Z' : t;
      return dayjs(new Date(utcStr)).format('MM-DD HH:mm');
    });
    const requests = trend.map(item => item.requests || item.count || 0);
    const bandwidth = trend.map(item => {
      const bytes = item.traffic || item.bandwidth || item.bytes || 0;
      return (bytes / 1024 / 1024).toFixed(2); // 转为 MB
    });

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: {
        data: [t('Requests'), t('Traffic (MB)')],
        top: 0,
        textStyle: { fontSize: isMobile ? 11 : 12 }
      },
      grid: {
        left: isMobile ? 40 : 50,
        right: isMobile ? 40 : 50,
        bottom: isMobile ? 25 : 30,
        top: isMobile ? 35 : 40,
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          fontSize: isMobile ? 10 : 12,
          formatter: (value) => {
            if (isMobile) {
              return value.slice(5); // 只显示 MM-DD
            }
            return value;
          }
        }
      },
      yAxis: [
        {
          type: 'value',
          name: t('Request'),
          position: 'left',
          axisLabel: { fontSize: isMobile ? 10 : 12 },
          nameTextStyle: { fontSize: isMobile ? 10 : 12 }
        },
        {
          type: 'value',
          name: 'MB',
          position: 'right',
          axisLabel: { fontSize: isMobile ? 10 : 12 },
          nameTextStyle: { fontSize: isMobile ? 10 : 12 }
        }
      ],
      series: [
        {
          name: t('Requests'),
          type: 'line',
          data: requests,
          smooth: true,
          itemStyle: { color: '#1890ff' },
          areaStyle: { color: 'rgba(24, 144, 255, 0.1)' }
        },
        {
          name: t('Traffic (MB)'),
          type: 'line',
          yAxisIndex: 1,
          data: bandwidth,
          smooth: true,
          itemStyle: { color: '#52c41a' },
          areaStyle: { color: 'rgba(82, 196, 26, 0.1)' }
        }
      ]
    };

    chartInstance.current.setOption(option);
  };

  const [purging, setPurging] = useState(false);

  const handlePurgeCache = () => {
    if (!domainData?.id) return;
    Modal.confirm({
      title: t('Purge cache'),
      content: t('Purge all cache for domain {{domain}}? The first request after the purge will fetch content from the origin.', { domain: domainData.domain }),
      okText: t('Clear'),
      cancelText: t('Cancel'),
      onOk: async () => {
        setPurging(true);
        try {
          const response = await cdnService.purgeCache(domainData.id);
          if (response.success) {
            message.success(t('Cache purge request submitted'));
          } else {
            message.error(response.error || t('Failed to purge cache'));
          }
        } catch (error) {
          message.error(t('Failed to purge cache: {{message}}', { message: error.message }));
        } finally {
          setPurging(false);
        }
      }
    });
  };

  const getStatusTag = (enabled) => {
    return enabled ? (
      <Tag icon={<CheckCircleOutlined />} color="success">{t('Enable')}</Tag>
    ) : (
      <Tag icon={<CloseCircleOutlined />} color="default">{t('Disable')}</Tag>
    );
  };

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isMobile ? 16 : 24,
        gap: 8
      }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 20 }}>{t('Domain overview')}</h2>
        <Button
          icon={<ClearOutlined />}
          size={isMobile ? 'small' : 'middle'}
          loading={purging}
          onClick={handlePurgeCache}
        >{t('Purge cache')}</Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Col xs={12} sm={12} md={8}>
          <Card size={isMobile ? 'small' : 'default'}>
            <Statistic
              title={t('Origin count')}
              value={domainData.hostnames?.length || 0}
              prefix={<GlobalOutlined />}
              valueStyle={{ fontSize: isMobile ? 20 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={8}>
          <Card size={isMobile ? 'small' : 'default'}>
            <Statistic
              title={t('Number of rules')}
              value={domainData.rules?.length || 0}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ fontSize: isMobile ? 20 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={8}>
          <Card size={isMobile ? 'small' : 'default'}>
            <Statistic
              title={t('Firewall')}
              value={domainData.waf_enabled ? t('Enabled') : t('Disabled')}
              prefix={<SafetyOutlined />}
              valueStyle={{
                color: domainData.waf_enabled ? '#3f8600' : '#999',
                fontSize: isMobile ? 16 : 24
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* 流量趋势图 */}
      <Card
        title={
          <span>
            <LineChartOutlined style={{ marginRight: 8 }} />{t('Traffic in the last 7 days')}</span>
        }
        style={{ marginBottom: isMobile ? 16 : 24 }}
        size={isMobile ? 'small' : 'default'}
        extra={
          <Button
            type="link"
            size={isMobile ? 'small' : 'middle'}
            onClick={() => navigate('traffic')}
          >{t('Details')}</Button>
        }
      >
        <Spin spinning={chartLoading}>
          <div style={{ position: 'relative' }}>
            <div
              ref={chartRef}
              style={{
                width: '100%',
                height: isMobile ? 200 : 300,
                minHeight: isMobile ? 200 : 300
              }}
            />
            {!chartLoading && !(trafficData?.timeline?.length || trafficData?.trend?.length) && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#999',
                fontSize: isMobile ? 12 : 14
              }}>{t('No traffic data yet')}</div>
            )}
          </div>
        </Spin>
      </Card>

      {/* 流量使用配额 */}
      {subscriptionInfo?.usage_stats && Object.keys(subscriptionInfo.usage_stats).length > 0 && (
        <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
          {Object.entries(subscriptionInfo.usage_stats).map(([featureKey, usage], index) => {
            const formatBytes = (bytes) => {
              if (bytes >= 1024 * 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024 / 1024).toFixed(2)} TB`;
              if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
              if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
              if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
              return `${bytes} B`;
            };
            const formatNumber = (num) => {
              if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
              if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
              return num.toString();
            };
            const isTraffic = featureKey === 'traffic_bytes' || featureKey === 'bandwidth_bytes';
            const title = isTraffic ? t('Billed traffic this period') : (featureKey === 'requests' ? t('Requests') : featureKey);
            const usedValue = usage.used ?? usage.used_value ?? 0;
            const limitValue = usage.limit === -1 ? 0 : (usage.limit ?? usage.limit_value ?? 0);
            const usedDisplay = isTraffic ? formatBytes(usedValue) : formatNumber(usedValue);
            const limitDisplay = usage.is_unlimited ? t('Unlimited') : (isTraffic ? formatBytes(limitValue) : formatNumber(limitValue));
            const percentage = usage.is_unlimited ? 0 : (limitValue > 0 ? Math.min(100, (usedValue / limitValue) * 100) : 0);
            const resetCycleLabels = { daily: t('Daily'), weekly: t('Weekly'), monthly: t('Monthly'), quarterly: t('Quarterly'), yearly: t('Yearly') };

            return (
              <Col xs={24} sm={12} key={index}>
                <Card size={isMobile ? 'small' : 'default'}>
                  <Statistic
                    title={
                      <span>
                        {title}
                        <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
                          {resetCycleLabels[usage.reset_cycle] || t('Monthly')}{t('Reset')}</Tag>
                      </span>
                    }
                    value={usedDisplay}
                    suffix={<span style={{ fontSize: 14, color: '#999' }}>/ {limitDisplay}</span>}
                    prefix={isTraffic ? <ThunderboltOutlined /> : <LineChartOutlined />}
                    valueStyle={{ fontSize: isMobile ? 16 : 24 }}
                  />
                  {!usage.is_unlimited && (
                    <Progress
                      percent={percentage.toFixed(1)}
                      status={percentage >= 100 ? 'exception' : percentage >= 80 ? 'exception' : 'normal'}
                      style={{ marginTop: 8 }}
                      size={isMobile ? 'small' : 'default'}
                    />
                  )}
                  {usage.period_start && usage.period_end && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>{t('Period: {{value}} ~ {{value2}}', { value: usage.period_start?.slice(5, 10), value2: usage.period_end?.slice(5, 10) })}</div>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* 资源配额卡片 */}
      {subscriptionInfo?.resource_quota_usage?.domains?.[domainData.id] && (
        <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
          {(() => {
            const quotas = subscriptionInfo.resource_quota_usage.domains[domainData.id].quotas || {};
            const hostQuota = quotas.cdn_hostnames_limit;
            const ruleQuota = quotas.cdn_rules_limit;
            const workerQuota = quotas.cdn_worker_routes_limit;

            return (
              <>
                {hostQuota && (
                  <Col xs={24} sm={12} md={8}>
                    <Card size={isMobile ? 'small' : 'default'}>
                      <Statistic
                        title={t('Origin hostname quota')}
                        value={`${hostQuota.used}/${hostQuota.limit === -1 ? t('Unlimited') : hostQuota.limit}`}
                        prefix={<GlobalOutlined />}
                        valueStyle={{ fontSize: isMobile ? 16 : 24 }}
                      />
                      {!hostQuota.is_unlimited && (
                        <Progress
                          percent={hostQuota.percentage}
                          status={hostQuota.percentage >= 100 ? 'exception' : hostQuota.percentage >= 80 ? 'exception' : 'normal'}
                          style={{ marginTop: 8 }}
                          size={isMobile ? 'small' : 'default'}
                        />
                      )}
                    </Card>
                  </Col>
                )}
                {ruleQuota && (
                  <Col xs={24} sm={12} md={8}>
                    <Card size={isMobile ? 'small' : 'default'}>
                      <Statistic
                        title={t('Page rule quota')}
                        value={`${ruleQuota.used}/${ruleQuota.limit === -1 ? t('Unlimited') : ruleQuota.limit}`}
                        prefix={<ThunderboltOutlined />}
                        valueStyle={{ fontSize: isMobile ? 16 : 24 }}
                      />
                      {!ruleQuota.is_unlimited && (
                        <Progress
                          percent={ruleQuota.percentage}
                          status={ruleQuota.percentage >= 100 ? 'exception' : ruleQuota.percentage >= 80 ? 'exception' : 'normal'}
                          style={{ marginTop: 8 }}
                          size={isMobile ? 'small' : 'default'}
                        />
                      )}
                    </Card>
                  </Col>
                )}
                {workerQuota && (
                  <Col xs={24} sm={12} md={8}>
                    <Card size={isMobile ? 'small' : 'default'}>
                      <Statistic
                        title={t('Worker route quota')}
                        value={`${workerQuota.used}/${workerQuota.limit === -1 ? t('Unlimited') : workerQuota.limit}`}
                        prefix={<ThunderboltOutlined />}
                        valueStyle={{ fontSize: isMobile ? 16 : 24 }}
                      />
                      {!workerQuota.is_unlimited && (
                        <Progress
                          percent={workerQuota.percentage}
                          status={workerQuota.percentage >= 100 ? 'exception' : workerQuota.percentage >= 80 ? 'exception' : 'normal'}
                          style={{ marginTop: 8 }}
                          size={isMobile ? 'small' : 'default'}
                        />
                      )}
                    </Card>
                  </Col>
                )}
              </>
            );
          })()}
        </Row>
      )}

      {/* 域名基本信息 */}
      <Card
        title={t('Basic information')}
        style={{ marginBottom: isMobile ? 16 : 24 }}
        size={isMobile ? 'small' : 'default'}
      >
        <Descriptions
          bordered
          column={isMobile ? 1 : 2}
          size={isMobile ? 'small' : 'default'}
          labelStyle={isMobile ? { width: '35%' } : undefined}
        >
          <Descriptions.Item label={t('Domains')}>
            <span style={{ wordBreak: 'break-all' }}>{domainData.domain}</span>
          </Descriptions.Item>
          <Descriptions.Item label={t('Created at')}>
            {domainData.created_at}
          </Descriptions.Item>
          <Descriptions.Item label={t('WAF firewall')}>
            {getStatusTag(domainData.waf_enabled)}
          </Descriptions.Item>
          {domainData.cloudflare_zone_id && (
            <>
              <Descriptions.Item label="Zone ID">
                <span style={{ wordBreak: 'break-all', fontSize: isMobile ? 12 : 14 }}>
                  {domainData.cloudflare_zone_id}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Account ID">
                <span style={{ wordBreak: 'break-all', fontSize: isMobile ? 12 : 14 }}>
                  {domainData.cloudflare_account_id || '-'}
                </span>
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      {/* 源站配置信息 */}
      <Card
        title={t('Origin configuration')}
        style={{ marginBottom: isMobile ? 16 : 24, overflow: 'hidden' }}
        size={isMobile ? 'small' : 'default'}
        styles={{ body: { overflow: 'hidden' } }}
        extra={
          <Button
            type="link"
            icon={<SettingOutlined />}
            size={isMobile ? 'small' : 'middle'}
            onClick={() => navigate('origin')}
          >
            {isMobile ? t('Configuration') : t('Configure origin')}
          </Button>
        }
      >
        {domainData.hostnames && domainData.hostnames.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16, overflow: 'hidden' }}>
            {domainData.hostnames.map((hostname, index) => (
              <Card
                key={index}
                type="inner"
                title={
                  isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' }}>
                      <span style={{ fontSize: 12, color: '#666' }}>{t('Origin {{value}}', { value: index + 1 })}</span>
                      <span style={{
                        wordBreak: 'break-all',
                        fontSize: 13,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {hostname.hostname}
                      </span>
                    </div>
                  ) : (
                    <span style={{
                      wordBreak: 'break-all',
                      fontSize: 14
                    }}>{t('Origin {{value}}: {{hostname}}', { value: index + 1, hostname: hostname.hostname })}</span>
                  )
                }
                size={isMobile ? 'small' : 'default'}
                extra={
                  <Tag color={hostname.enabled ? 'green' : 'default'} style={{ flexShrink: 0 }}>
                    {hostname.enabled ? t('Enable') : t('Disable')}
                  </Tag>
                }
                style={{ overflow: 'hidden' }}
                styles={isMobile ? {
                  header: { padding: '8px 12px', overflow: 'hidden' },
                  body: { padding: '12px', overflow: 'hidden' }
                } : undefined}
              >
                {isMobile ? (
                  // 移动端使用更紧凑的布局
                  <div style={{ fontSize: 12, overflow: 'hidden' }}>
                    <div style={{ marginBottom: 8, overflow: 'hidden' }}>
                      <div style={{ color: '#666', marginBottom: 4 }}>{t('Origin address:')}</div>
                      <div style={{ wordBreak: 'break-all', background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, overflow: 'hidden' }}>
                        {hostname.port ? formatOrigins(hostname.origin).split(', ').map((a) => `${a}:${hostname.port}`).join(', ') : formatOrigins(hostname.origin)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, overflow: 'hidden' }}>
                      <Tag>{hostname.protocol}</Tag>
                      <Tag>{hostname.mode}</Tag>
                      {hostname.http_version && <Tag>HTTP/{hostname.http_version}</Tag>}
                      {hostname.websocket_enabled && <Tag color="blue">WebSocket</Tag>}
                    </div>
                  </div>
                ) : (
                  <Descriptions
                    column={2}
                    size="small"
                  >
                    <Descriptions.Item label={t('Origin address')}>
                      <span style={{ wordBreak: 'break-all' }}>
                        {hostname.port ? formatOrigins(hostname.origin).split(', ').map((a) => `${a}:${hostname.port}`).join(', ') : formatOrigins(hostname.origin)}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Protocol')}>
                      {hostname.protocol}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Load balancing')}>
                      {hostname.mode}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('HTTP version')}>
                      {hostname.http_version}
                    </Descriptions.Item>
                    <Descriptions.Item label="WebSocket">
                      {hostname.websocket_enabled == null ? <Tag>{t('Follow domain setting')}</Tag> : getStatusTag(hostname.websocket_enabled)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Proxy timeout')}>{t('{{proxy_timeout}} seconds', { proxy_timeout: hostname.proxy_timeout })}</Descriptions.Item>
                    <Descriptions.Item label={t('Priority')}>
                      {hostname.priority}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <Alert
            message={t('No origin configured')}
            description={t('Please configure at least one origin server first')}
            type="info"
            showIcon
          />
        )}
      </Card>

      {/* 页面规则信息 */}
      <Card
        title={t('Page rules')}
        size={isMobile ? 'small' : 'default'}
        extra={
          <Button
            type="link"
            icon={<EditOutlined />}
            size={isMobile ? 'small' : 'middle'}
            onClick={() => navigate('rules')}
          >
            {isMobile ? t('Edit') : t('Edit rule')}
          </Button>
        }
      >
        {domainData.rules && domainData.rules.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {domainData.rules.slice(0, isMobile ? 3 : 5).map((rule, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  background: '#fafafa',
                  borderRadius: 4
                }}
              >
                <span style={{ wordBreak: 'break-all', flex: 1, minWidth: 0 }}>
                  {rule.name}
                </span>
                <Tag color="blue" style={{ marginLeft: 8, flexShrink: 0 }}>{t('Priority: {{priority}}', { priority: rule.priority })}</Tag>
              </div>
            ))}
            {domainData.rules.length > (isMobile ? 3 : 5) && (
              <div style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>{t('{{value}} more rules...', { value: domainData.rules.length - (isMobile ? 3 : 5) })}</div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>{t('No page rules yet. Click "Edit rules" to add one.')}</div>
        )}
      </Card>
    </div>
  );
}
